/**
 * Category Validation Utilities
 *
 * This file contains all validation logic for different category types.
 * Each validation function follows a consistent pattern and uses shared helper functions
 * to reduce code duplication and ensure consistent validation behavior.
 *
 * Validation Functions by Category Type:
 * - validateGeneralCategoryData: For birthday and wedding categories (name + single date)
 * - validateEventsAndLocationsData: For events (flexible requirements)
 * - validateCollegeCategoryData: For education (name + date range, no future dates)
 * - validateVacationCategoryData: For leisure (name + dates/location)
 * - validateCustomCategoryData: For custom categories (flexible requirements)
 * - validateSimpleCategoryData: For people categories (name only)
 *
 * Helper Functions:
 * - validateCategoryName: Base name validation
 * - validateDateRange: Date range validation with future date control
 * - validateLocation: Location text validation
 */

/**
 * Base validation for category name
 * @param {string} name - Category name to validate
 * @param {function} setMessage - Function to set error message
 * @param {string} fieldName - Name of the field for error messages (default: 'Name')
 * @returns {boolean} - True if valid, false otherwise
 */
const validateCategoryName = (name, setMessage, fieldName = 'Name') => {
  if (!name || name.trim() === '') {
    setMessage(`${fieldName} cannot be empty`)
    return false
  }

  if (name.length > 100) {
    setMessage(`${fieldName} is too long (max 100 characters)`)
    return false
  }

  return true
}

/**
 * Validates date range ensuring fromDate is not after toDate
 * @param {string} fromDate - Start date in ISO format
 * @param {string} toDate - End date in ISO format
 * @param {function} setMessage - Function to set error message
 * @param {boolean} allowFuture - Whether future dates are allowed (default: true)
 * @returns {boolean} - True if valid, false otherwise
 */
const validateDateRange = (
  fromDate,
  toDate,
  setMessage,
  allowFuture = true
) => {
  if (!fromDate && !toDate) return true // Both dates optional

  const fromDateObj = fromDate ? new Date(fromDate) : null
  const toDateObj = toDate ? new Date(toDate) : null
  const currentDate = new Date()

  // Check for future dates if not allowed
  if (!allowFuture) {
    if (fromDateObj && fromDateObj > currentDate) {
      setMessage('Start date cannot be in the future')
      return false
    }
    if (toDateObj && toDateObj > currentDate) {
      setMessage('End date cannot be in the future')
      return false
    }
  }

  // Validate date range
  if (fromDateObj && toDateObj && fromDateObj > toDateObj) {
    setMessage('Start date cannot be after end date')
    return false
  }

  return true
}

/**
 * Validates location text
 * @param {string} location - Location text to validate
 * @param {function} setMessage - Function to set error message
 * @returns {boolean} - True if valid, false otherwise
 */
const validateLocation = (location, setMessage) => {
  if (!location) return true // Location is optional

  if (location.trim() === '') {
    setMessage('Location cannot be empty')
    return false
  }

  if (location.length > 200) {
    setMessage('Location is too long (max 200 characters)')
    return false
  }

  return true
}

/**
 * Validates general category data (name and single date)
 * Used for: Birthday categories
 */
export const validateGeneralCategoryData = (data, setMessage) => {
  // Validate name
  if (!validateCategoryName(data.name, setMessage)) {
    return false
  }

  // Date validation - single date required
  if (!data.calendarAttrs?.fromDate) {
    setMessage('Date is required')
    return false
  }

  return validateDateRange(data.calendarAttrs.fromDate, null, setMessage, false)
}

/**
 * Validates events and locations category data
 * Used for: Events and locations categories
 */
export const validateEventsAndLocationsData = (data, setMessage) => {
  // At least one field must be provided
  if (!data.name && !data.calendarAttrs && !data.location) {
    setMessage('At least one field must be filled out')
    return false
  }

  // Validate name if provided
  if (data.name && !validateCategoryName(data.name, setMessage, 'Title')) {
    return false
  }

  // Handle single-day events by setting both dates to the same value
  if (data.calendarAttrs) {
    const { fromDate, toDate } = data.calendarAttrs

    if (fromDate && !toDate) {
      data.calendarAttrs.toDate = fromDate
    }
    if (toDate && !fromDate) {
      data.calendarAttrs.fromDate = toDate
    }

    // Validate date range
    if (
      !validateDateRange(
        data.calendarAttrs.fromDate,
        data.calendarAttrs.toDate,
        setMessage
      )
    ) {
      return false
    }
  }

  // Validate location if provided
  if (
    data.location?.text &&
    !validateLocation(data.location.text, setMessage)
  ) {
    return false
  }

  return true
}

/**
 * Validates college category data (requires both start and end dates)
 * Used for: College categories
 */
export const validateCollegeCategoryData = (data, setMessage) => {
  // Validate name
  if (!validateCategoryName(data.name, setMessage, 'College name')) {
    return false
  }

  // Both dates are required for college categories
  if (!data.calendarAttrs?.fromDate || !data.calendarAttrs?.toDate) {
    setMessage('Both start and end dates are required')
    return false
  }

  // College dates should not be in the future
  return validateDateRange(
    data.calendarAttrs.fromDate,
    data.calendarAttrs.toDate,
    setMessage,
    false
  )
}

/**
 * Validates vacation category data (flexible requirements)
 * Used for: Vacation categories
 */
export const validateVacationCategoryData = (data, setMessage) => {
  // Validate name
  if (!validateCategoryName(data.name, setMessage, 'Vacation title')) {
    return false
  }

  // At least one field (dates or location) is required
  if (
    !data.calendarAttrs?.fromDate &&
    !data.calendarAttrs?.toDate &&
    !data.calendarAttrs?.location
  ) {
    setMessage('Please provide at least one of: dates or location')
    return false
  }

  // Validate location if provided
  if (
    data.calendarAttrs?.location &&
    !validateLocation(data.calendarAttrs.location, setMessage)
  ) {
    return false
  }

  // Validate date range if provided
  return validateDateRange(
    data.calendarAttrs?.fromDate,
    data.calendarAttrs?.toDate,
    setMessage
  )
}

/**
 * Validates custom category data (flexible requirements)
 * Used for: Custom categories
 */
export const validateCustomCategoryData = (data, setMessage) => {
  // Validate name
  if (!validateCategoryName(data.name, setMessage, 'Category name')) {
    return false
  }

  // At least one field (dates or location) should be provided
  if (
    !data.calendarAttrs?.fromDate &&
    !data.calendarAttrs?.toDate &&
    !data.calendarAttrs?.location
  ) {
    setMessage('Please provide at least one of: dates or location')
    return false
  }

  // Validate location if provided
  if (
    data.calendarAttrs?.location &&
    !validateLocation(data.calendarAttrs.location, setMessage)
  ) {
    return false
  }

  // Validate date range if provided
  return validateDateRange(
    data.calendarAttrs?.fromDate,
    data.calendarAttrs?.toDate,
    setMessage
  )
}

/**
 * Validates simple category data (name only)
 * Used for: Friends & Family, Pets categories
 */
export const validateSimpleCategoryData = (data, setMessage) => {
  return validateCategoryName(data.name, setMessage)
}

// Export helper functions for advanced use cases
export { validateCategoryName, validateDateRange, validateLocation }
